import { httpClient } from './httpClient';

// Event explanation types
export interface EventAnnotation {
  /** Exact text span from the event headline or summary. */
  text: string;
  /** How this text relates to the source: verbatim, summarised, synthesised. */
  provenance: 'verbatim' | 'summarised' | 'synthesised';
  /** Human-readable source attribution, e.g. "Dr Smith" or "Ambulance crew notes". */
  sourceLabel: string | null;
  /** One-sentence explanation of what the source is and how the text was derived. */
  sourceDescription: string | null;
  /** Raw document filename (for viewer lookup). */
  documentName: string | null;
  /** Resolved document ID for opening the viewer. */
  documentId: string | null;
  /** Page number in the source document. */
  pageNumber: number | null;
}

export interface EventExplanationResponse {
  annotations: EventAnnotation[];
  sourceSummary?: string;
}

// Types for chronology API - aligned with backend DTOs
export interface ChronologyEvent {
  eventId: string;
  originalEventId: string;
  eventType: string;
  significance?: 'MAJOR' | 'SUPPORTING' | 'ROUTINE';
  headline?: string;
  clinician?: string;
  location?: string;
  description: string;
  eventDate: string; // LocalDateTime from backend
  dateSource: string;
  position: number;
  confidence: number;
  validationStatus: string;
  requiresValidation: boolean;
  createdAt: string;
  updatedAt: string;
  lastModifiedBy: string;
  originalHeadline?: string;
  originalSummary?: string;
  originalDescription?: string;
  originalEventDate?: string;
  originalEventType?: string;
  isEdited?: boolean;
  citations?: CitationResponse[];
}

export interface CitationResponse {
  citationId: string;
  documentId: string;
  documentName: string;
  pageNumber: number;
  characterRange: {
    start: number;
    end: number;
  };
  contextText: string;
  integrityHash: string;
  createdAt: string;
  updatedAt: string;
}

export interface NarrativeSection {
  title: string;
  summary: string;
  eventIds: string[];
  isExpertSection?: boolean;
}

export interface ChronologyNarrative {
  overviewSummary: string;
  indexEventId: string;
  sections: NarrativeSection[];
}

export interface ChronologyResponse {
  id: string;
  caseId: string;
  userId: string;
  patientName: string;
  description?: string;
  chronologyType?: 'REVIEW' | 'OBJECTIVE';
  validationStatus: 'DRAFT' | 'VALIDATED' | 'INVALID';
  confidenceScore: number;
  events: ChronologyEvent[];
  narrative?: ChronologyNarrative;
  createdAt: string;
  updatedAt: string;
  lastValidatedAt?: string;
}

// Timeline types for frontend compatibility
export interface MedicalEvent {
  eventId: string;
  type: string;
  significance?: 'MAJOR' | 'SUPPORTING' | 'ROUTINE';
  title?: string;
  summary?: string;
  clinician?: string;
  location?: string;
  description: string;
  timestamp: string | { start: string; end: string };
  certainty: string;
  extractionConfidence: number;
  validationStatus?: string;
  lastModifiedBy?: string;
  originalHeadline?: string;
  originalSummary?: string;
  originalDescription?: string;
  originalEventDate?: string;
  originalEventType?: string;
  isEdited?: boolean;
  sourceReference: {
    documentId: string;
    documentName: string;
    pageNumber: number;
    startPosition: number;
    endPosition: number;
    characterStart: number;
    characterEnd: number;
  };
  citations?: Array<{
    citationId: string;
    documentId: string;
    documentName: string;
    pageNumber: number;
    startPosition: number;
    endPosition: number;
    sourceText: string;
    contextText: string;
    confidence: number;
    isValid: boolean;
  }>;
}

export interface Timeline {
  caseId: string;
  events: MedicalEvent[];
  gaps: TemporalGap[];
  conflicts: EventConflict[];
  narrative?: ChronologyNarrative;
  lastUpdated: string;
}

export interface TemporalGap {
  duration: string;
  severity: string;
  startEvent?: string;
  endEvent?: string;
}

export interface EventConflict {
  description: string;
  severity: string;
  conflictingEvents: string[];
}

// Chronology API service
export const chronologyApi = {
  // Get chronology by case ID (main method for timeline integration)
  getChronologyByCaseId: async (caseId: string, type?: 'REVIEW' | 'OBJECTIVE'): Promise<ChronologyResponse> => {

    try {
      const queryParam = type ? `?type=${type}` : '';
      const response = await httpClient.get<ChronologyResponse>(`/chronologies/case/${caseId}${queryParam}`);
      return response;
    } catch (error: any) {
      
      // If it's a 404, it means no chronology exists yet - this is expected
      if (error.message && error.message.includes('404')) {
        // Return a minimal empty chronology structure
        return {
          id: '',
          caseId: caseId,
          userId: '',
          patientName: 'Unknown Patient',
          validationStatus: 'DRAFT' as const,
          confidenceScore: 0,
          events: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      }
      
      // For other errors, log and re-throw
      console.error('❌ Failed to get chronology:', error);
      throw error;
    }
  },

  // Correct an event (partial update)
  correctEvent: async (eventId: string, correction: {
    headline?: string;
    summary?: string;
    description?: string;
    eventDate?: string;
    eventType?: string;
    correctionReason: string;
  }): Promise<any> => {
    return httpClient.patch(`/chronologies/events/${eventId}`, correction);
  },

  // Revert an event to its original AI-extracted content
  revertEvent: async (eventId: string): Promise<any> => {
    return httpClient.post(`/chronologies/events/${eventId}/revert`, {});
  },

  explainEvent: async (eventId: string): Promise<EventExplanationResponse> => {
    return httpClient.post<EventExplanationResponse>(`/chronologies/events/${eventId}/explain`, {});
  },

  // Convert chronology response to timeline format for UI compatibility
  convertToTimeline: (chronology: ChronologyResponse): Timeline => {
    
    if (chronology.events && chronology.events.length > 0) {
    }

    const events: MedicalEvent[] = chronology.events.map(event => {
      // Convert citations to frontend format
      const citations = event.citations?.map(citation => ({
        citationId: citation.citationId,
        documentId: citation.documentId,
        documentName: citation.documentName,
        pageNumber: citation.pageNumber,
        startPosition: citation.characterRange?.start || 0,
        endPosition: citation.characterRange?.end || 0,
        sourceText: citation.contextText || '',
        contextText: citation.contextText || '',
        confidence: 0.95, // Default confidence for citations
        isValid: true
      })) || [];


      return {
        eventId: event.eventId,
        type: event.eventType.toLowerCase(),
        significance: event.significance || undefined,
        title: event.headline || undefined,
        summary: (event as any).summary || undefined,
        clinician: event.clinician || undefined,
        location: event.location || undefined,
        description: event.description,
        timestamp: event.eventDate, // Backend uses eventDate, frontend expects timestamp
        certainty: event.dateSource || 'inferred', // Map dateSource to certainty
        extractionConfidence: event.confidence || 0, // Backend uses confidence, frontend expects extractionConfidence
        validationStatus: event.validationStatus || undefined,
        lastModifiedBy: event.lastModifiedBy || undefined,
        originalHeadline: event.originalHeadline || undefined,
        originalSummary: event.originalSummary || undefined,
        originalDescription: event.originalDescription || undefined,
        originalEventDate: event.originalEventDate || undefined,
        originalEventType: event.originalEventType || undefined,
        isEdited: event.isEdited || false,
        sourceReference: {
          documentId: event.citations?.[0]?.documentId || '',
          documentName: event.citations?.[0]?.documentName || 'Unknown document',
          pageNumber: event.citations?.[0]?.pageNumber || 0,
          startPosition: event.citations?.[0]?.characterRange?.start || 0,
          endPosition: event.citations?.[0]?.characterRange?.end || 0,
          characterStart: event.citations?.[0]?.characterRange?.start || 0,
          characterEnd: event.citations?.[0]?.characterRange?.end || 0
        },
        citations: citations
      };
    });

    if (events.length > 0) {
    }

    // Real gaps and conflicts will come from chronology service analysis
    const gaps: TemporalGap[] = [];
    const conflicts: EventConflict[] = [];

    return {
      caseId: chronology.caseId,
      events,
      gaps,
      conflicts,
      narrative: chronology.narrative || undefined,
      lastUpdated: chronology.updatedAt
    };
  },

  // Regenerate the narrative summary for a chronology
  regenerateNarrative: async (chronologyId: string): Promise<ChronologyResponse> => {
    return httpClient.post<ChronologyResponse>(`/chronologies/${chronologyId}/narrative/regenerate`, {});
  },

  // Execute a natural language query against chronology events
  queryChronology: async (chronologyId: string, query: string): Promise<ChronologyQueryResponse> => {
    return httpClient.post<ChronologyQueryResponse>(`/chronologies/${chronologyId}/query`, { query });
  },
};

// Natural language query response types
// SUMMARISE and CONSOLIDATE are returned by the LLM to signal the frontend
// should redirect the user to the AI assistant sidebar.
export type QueryIntent = 'FILTER' | 'SORT' | 'SUMMARISE' | 'CONSOLIDATE';

export interface ChronologyQueryResponse {
  intent: QueryIntent;
  eventIds: string[];
  summary?: string;
  sortDescription?: string;
  appliedFilters?: string;
}